/**
 * The cart drawer and the cart page: what snippets olin-cart-suggest, olin-cart-options and
 * olin-cart-extras put there.
 * - «+» on «Ще знадобиться» puts one into the cart; the theme then renders the drawer or the page
 *   again with the new cart.
 * - «Вже маю» hides a rule for good, in the same list as the line on «Барний інвентар»
 *   (assets/olin-catalog.js), and the next rule shows.
 * - The gift and documents switches, their ticks and fields and the comment go into the cart at once,
 *   as attributes, so they reach the order. The order note gets them too, as readable lines: the packing
 *   slip prints the note but not the attributes. The inputs also belong to the cart form, so the
 *   checkout button sends them as well.
 * - «Хто отримає посилку?»: «Той, кому дарую» asks the recipient's name and phone, and the checkout
 *   waits until both are there.
 * - The dispatch line is worked out again by Kyiv time in the visitor's browser.
 * - The checkout gets the postal code «00000» from the cart, so it lists the delivery methods at once.
 * The theme renders the cart again after every change, so everything works by delegation, and an
 * observer puts the visitor's state back into what was rendered.
 */
import { CartLinesUpdateEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

const SOURCE = 'olin-cart';
/** The rules marked «Вже маю», as a JSON list of handles; shared with assets/olin-catalog.js */
const SKIPPED = 'olin.cartHints.skipped';
/** Gift options ticked when «Це подарунок?» is switched on */
const GIFT_DEFAULTS = ['Подарункова коробка', 'Без цін у посилці'];
/** A pause after typing before a field goes to the cart, ms */
const TYPING_PAUSE = 600;
/** The fields of «Той, кому дарую»: the parcel goes to this name and phone */
const RECIPIENT = ['ПІБ отримувача', 'Телефон отримувача'];

/** @returns {string[]} */
function cartSectionIds() {
  return [...document.querySelectorAll('cart-items-component')]
    .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId ?? '' : ''))
    .filter(Boolean);
}

/* ───── «+» on «Ще знадобиться» ───── */

/** @param {HTMLElement} button */
async function add(button) {
  const id = button.dataset.olinCartAdd;
  if (!id || button.getAttribute('aria-busy') === 'true') return;
  button.setAttribute('aria-busy', 'true');

  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart',
      lines: [{ merchandiseId: id, quantity: 1 }],
      promise: deferred.promise,
    })
  );

  try {
    // The offered products aren't in the cart yet, so «1» is one more; the answer is the whole cart
    const response = await fetch(
      `${Theme.routes.cart_update_url}.js`,
      fetchConfig('json', {
        body: JSON.stringify({
          updates: { [id]: 1 },
          sections: cartSectionIds().join(','),
          sections_url: window.location.pathname,
        }),
      })
    );
    const data = await response.json();
    const failed = !response.ok || Boolean(data.status);
    const cart = failed ? await (await fetch(`${Theme.routes.cart_url}.js`)).json() : data;
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: SOURCE,
        sourceId: id,
        itemCount: 1,
        didError: failed,
        sections: failed ? undefined : data.sections,
      },
    });
  } catch (error) {
    deferred.reject(error);
  } finally {
    button.removeAttribute('aria-busy');
  }
}

/* ───── «Вже маю» ───── */

/** @returns {Set<string>} */
function skipped() {
  try {
    const list = JSON.parse(localStorage.getItem(SKIPPED) ?? '[]');
    return new Set(Array.isArray(list) ? list.map(String) : []);
  } catch (_) {
    return new Set();
  }
}

/** @param {string} handle */
function skip(handle) {
  const list = skipped();
  list.add(handle);
  try {
    localStorage.setItem(SKIPPED, JSON.stringify([...list]));
  } catch (_) {
    // Storage is off: the rule hides on this page only
  }
  showRules(list);
}

/** In each «Ще знадобиться» the first rule the visitor hasn't marked shows; none left, no block */
function showRules(skip = skipped()) {
  for (const block of document.querySelectorAll('[data-olin-cart-suggest]')) {
    if (!(block instanceof HTMLElement)) continue;
    const groups = [...block.querySelectorAll('[data-olin-cart-rule]')];
    if (!groups.length) continue;
    let shown = false;
    for (const group of groups) {
      if (!(group instanceof HTMLElement)) continue;
      const show = !shown && !skip.has(group.dataset.olinCartRule ?? '');
      if (group.hidden === show) group.hidden = !show;
      if (show) shown = true;
    }
    if (block.hidden === shown) block.hidden = !shown;
  }
}

/* ───── Attributes and the note ───── */

/** @type {Record<string, string>} */
let pendingAttributes = {};
/** @type {string | undefined} */
let pendingNote;
let timer = 0;
let saving = Promise.resolve();

/** @param {number} [delay] */
function save(delay = 0) {
  window.clearTimeout(timer);
  timer = window.setTimeout(() => flush(), delay);
}

/**
 * Sends what has changed, one request at a time.
 * @param {boolean} [leaving] - The page is going away: the request outlives it
 */
function flush(leaving = false) {
  window.clearTimeout(timer);
  /** @type {{ attributes?: Record<string, string>, note?: string }} */
  const body = {};
  if (Object.keys(pendingAttributes).length) body.attributes = pendingAttributes;
  if (pendingNote !== undefined) body.note = pendingNote;
  pendingAttributes = {};
  pendingNote = undefined;
  if (!body.attributes && body.note === undefined) return saving;

  const send = () =>
    fetch(`${Theme.routes.cart_update_url}.js`, {
      ...fetchConfig('json', { body: JSON.stringify(body) }),
      keepalive: leaving,
    }).catch(() => {});
  saving = leaving ? send() : saving.then(send);
  return saving;
}

/**
 * The order note: what the packer has to know, in plain Ukrainian lines, then the visitor's comment.
 * @param {Element} options - the block of snippet olin-cart-options
 */
function noteOf(options) {
  /** @param {string} key */
  const field = (key) => options.querySelector(`[data-olin-attr="${key}"]`);
  /** @param {string} key */
  const ticked = (key) => {
    const input = field(key);
    return input instanceof HTMLInputElement && input.checked;
  };
  /** @param {string} key */
  const text = (key) => {
    const input = field(key);
    return input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement ? input.value.trim() : '';
  };
  /** @param {string} name */
  const on = (name) => Boolean(options.querySelector(`[data-olin-opt="${name}"].is-on`));

  const lines = [];
  const linePhoto = options.hasAttribute('data-line-photo');
  if (on('gift')) {
    // First line, so the manager sees it before anything else: whom the parcel goes to
    const flag = options.querySelector('[data-olin-who-flag]');
    if (flag instanceof HTMLInputElement && flag.value === 'Так') {
      const who = RECIPIENT.map(text).filter(Boolean).join(', ');
      lines.push(`Інший отримувач (для ТТН): ${who || 'дані не вказано'}`);
    }
    const parts = [];
    if (ticked('Подарункова коробка')) parts.push('коробка');
    if (ticked('Фото перед відправкою') || linePhoto) parts.push('фото перед відправкою');
    if (ticked('Без цін у посилці')) parts.push('без цін у посилці');
    lines.push(parts.length ? `Подарунок: ${parts.join(', ')}` : 'Подарунок');
  } else if (linePhoto) {
    lines.push('Фото перед відправкою');
  }
  if (on('business')) {
    const parts = [];
    if (text('Рахунок на')) parts.push(text('Рахунок на'));
    if (text('ЄДРПОУ / ІПН')) parts.push(`ЄДРПОУ / ІПН ${text('ЄДРПОУ / ІПН')}`);
    parts.push(ticked('Паперові документи') ? 'паперові документи в посилку' : 'документи в PDF');
    lines.push(`Документи для ФОП/ТОВ: ${parts.join(', ')}`);
  }
  const comment = text('Коментар');
  if (comment) lines.push(lines.length ? `Коментар: ${comment}` : comment);
  return lines.join('\n');
}

/**
 * Puts the summary into the note when it no longer matches the options.
 * @param {Element} options
 */
function syncNote(options) {
  const summary = options.querySelector('[data-olin-note-summary]');
  if (!(summary instanceof HTMLInputElement)) return false;
  const note = noteOf(options);
  if (summary.value === note) return false;
  summary.value = note;
  pendingNote = note;
  return true;
}

/** @param {Element} element - inside a block of options */
function noteChanged(element, delay = 0) {
  const options = element.closest('[data-olin-cart-options]');
  if (options && syncNote(options)) save(delay);
}

/** @param {HTMLElement} head - the switch of «Це подарунок?» or «Документи для ФОП/ТОВ» */
function toggle(head) {
  const box = head.closest('[data-olin-opt]');
  if (!(box instanceof HTMLElement)) return;
  const on = !box.classList.contains('is-on');
  box.classList.toggle('is-on', on);
  head.setAttribute('aria-checked', String(on));
  const body = box.querySelector('.olin-cart-opt__body');
  if (body instanceof HTMLElement) body.hidden = !on;

  const flag = box.querySelector('[data-olin-attr-flag]');
  if (flag instanceof HTMLInputElement) {
    flag.value = on ? 'Так' : '';
    pendingAttributes[flag.dataset.olinAttrFlag ?? ''] = flag.value;
  }

  // Switched on: the usual gift ticks; switched off: nothing of the group goes with the order
  for (const input of box.querySelectorAll('[data-olin-attr]')) {
    if (!(input instanceof HTMLInputElement)) continue;
    const key = input.dataset.olinAttr ?? '';
    if (input.type === 'checkbox') {
      input.checked = on ? input.checked || (box.dataset.olinOpt === 'gift' && GIFT_DEFAULTS.includes(key)) : false;
      pendingAttributes[key] = input.checked ? 'Так' : '';
    } else if (!on) {
      input.value = '';
      pendingAttributes[key] = '';
    }
  }
  if (!on && box.querySelector('[data-olin-who-flag]')) setWho(box, false);
  syncNote(box.closest('[data-olin-cart-options]') ?? box);
  save();
}

/**
 * «Хто отримає посилку?»: «Я» or «Той, кому дарую». Typed name and phone stay on the page, but go to
 * the cart (and with the cart form) only for «Той, кому дарую».
 * @param {Element} box - the block of «Це подарунок?»
 * @param {boolean} other - «Той, кому дарую»
 */
function setWho(box, other) {
  for (const button of box.querySelectorAll('[data-olin-who]')) {
    button.setAttribute('aria-checked', String((button.getAttribute('data-olin-who') === 'other') === other));
  }
  const flag = box.querySelector('[data-olin-who-flag]');
  if (flag instanceof HTMLInputElement) {
    flag.value = other ? 'Так' : '';
    pendingAttributes['Інший отримувач'] = flag.value;
  }
  const fields = box.querySelector('[data-olin-rec]');
  if (fields instanceof HTMLElement) fields.hidden = !other;
  for (const key of RECIPIENT) {
    const input = box.querySelector(`[data-olin-attr="${key}"]`);
    if (!(input instanceof HTMLInputElement)) continue;
    input.disabled = !other;
    pendingAttributes[key] = other ? input.value.trim() : '';
  }
  recipientNeed(box, false);
}

/**
 * Shows or hides «Вкажіть ім’я й телефон отримувача» and marks the empty fields.
 * @param {Element} box - the block of «Це подарунок?»
 * @param {boolean} show
 * @returns {HTMLInputElement | null} the first empty field, when shown
 */
function recipientNeed(box, show) {
  /** @type {HTMLInputElement | null} */
  let first = null;
  for (const key of RECIPIENT) {
    const input = box.querySelector(`[data-olin-attr="${key}"]`);
    if (!(input instanceof HTMLInputElement)) continue;
    const empty = show && !input.value.trim();
    if (empty) input.setAttribute('aria-invalid', 'true');
    else input.removeAttribute('aria-invalid');
    if (empty && !first) first = input;
  }
  const need = box.querySelector('[data-olin-rec-need]');
  if (need instanceof HTMLElement) need.hidden = !first;
  return first;
}

/**
 * A gift that goes straight to someone needs their name and phone, or the carrier won't take it.
 * @param {Element} button - the checkout button
 * @returns {boolean} the cart may go to the checkout
 */
function recipientReady(button) {
  // The options next to this button: the drawer's or the page's
  /** @type {Element | null} */
  let options = null;
  for (let node = button.parentElement; node && !options; node = node.parentElement) {
    options = node.querySelector('[data-olin-cart-options]');
  }
  const box = options?.querySelector('[data-olin-opt="gift"].is-on');
  const flag = box?.querySelector('[data-olin-who-flag]');
  if (!box || !(flag instanceof HTMLInputElement) || flag.value !== 'Так') return true;
  const empty = recipientNeed(box, true);
  if (!empty) return true;
  empty.focus();
  return false;
}

/**
 * A Ukrainian number is +380 and 9 more digits (or 0 and 9): a gentle hint, since a gift may go abroad.
 * @param {HTMLInputElement} input
 * @param {boolean} done - the visitor has left the field
 */
function checkPhone(input, done) {
  const hint = input.parentElement?.querySelector('[data-olin-phone-check]');
  if (!(hint instanceof HTMLElement)) return;
  const digits = input.value.replace(/\D/g, '');
  const valid = digits === '' || /^(?:380|0)\d{9}$/.test(digits);
  if (valid) hint.hidden = true;
  else if (done) hint.hidden = false;
}

/**
 * ЄДРПОУ has 8 digits, ІПН 10: a gentle hint, the order goes anyway.
 * @param {HTMLInputElement} input
 * @param {boolean} done - the visitor has left the field
 */
function checkCode(input, done) {
  const hint = input.parentElement?.querySelector('[data-olin-code-check]');
  if (!(hint instanceof HTMLElement)) return;
  const digits = input.value.replace(/\s/g, '');
  const valid = digits === '' || /^(\d{8}|\d{10})$/.test(digits);
  if (valid) hint.hidden = true;
  else if (done) hint.hidden = false;
}

/* ───── When we send the order ───── */

/** @returns {'today' | 'tomorrow' | 'monday'} by Kyiv time, the same rule as snippet olin-cart-extras */
function dispatchDay(now = new Date()) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Kyiv',
    weekday: 'short',
    hour: 'numeric',
    hourCycle: 'h23',
  }).formatToParts(now);
  const day = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].indexOf(parts.find((part) => part.type === 'weekday')?.value ?? '') + 1;
  const hour = Number(parts.find((part) => part.type === 'hour')?.value ?? 0);
  if (day >= 1 && day <= 5 && hour < 17) return 'today';
  if (day >= 1 && day <= 4) return 'tomorrow';
  return 'monday';
}

function showDispatch() {
  let day;
  try {
    day = dispatchDay();
  } catch (_) {
    return;
  }
  for (const line of document.querySelectorAll('[data-olin-dispatch]')) {
    if (!(line instanceof HTMLElement)) continue;
    const text = line.dataset[day];
    const target = line.querySelector('[data-olin-dispatch-text]');
    if (text && target && target.textContent !== text) target.textContent = text;
  }
}

/* ───── The postal code for the checkout ───── */

/** The cart that has the code already, kept for the browser tab */
const ZIP_DONE = 'olin.checkoutZip';
/** How long the checkout button waits for the code, ms */
const ZIP_WAIT = 1500;
let zipCart = '';
/** @type {Promise<void> | undefined} */
let zipping;

/** @param {string} id */
function zipDone(id) {
  if (zipCart === id) return true;
  try {
    if (sessionStorage.getItem(ZIP_DONE) !== id) return false;
  } catch (_) {
    return false;
  }
  zipCart = id;
  return true;
}

/**
 * The code and the visitor's cart (its Storefront API id, from the cart cookie), when the cart still
 * has to give it. The checkout button carries the code for a buyer in Ukraine whose account has no
 * address (snippet cart-summary).
 * @returns {{ zip: string, id: string } | null}
 */
function zipToGive() {
  const button = document.querySelector('[data-olin-zip]');
  const zip = button instanceof HTMLElement ? button.dataset.olinZip ?? '' : '';
  const cookie = document.cookie.match(/(?:^|;\s*)cart=([^;]+)/);
  if (!zip || !cookie) return null;
  const id = `gid://shopify/Cart/${decodeURIComponent(cookie[1])}`;
  return zipDone(id) ? null : { zip, id };
}

/**
 * @param {string} query
 * @param {Record<string, unknown>} variables
 */
async function storefront(query, variables) {
  const response = await fetch('/api/2026-07/graphql.json', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ query, variables }),
  });
  return (await response.json()).data;
}

/**
 * A Ukrainian address needs a postal code, and Shopify lists the delivery methods only after it.
 * Nova Poshta doesn't need one and buyers seldom know theirs. So the cart hands the checkout an
 * address with the country and «00000» only, through the Storefront API (it needs no token): the
 * checkout opens with the code filled in and the methods already listed. The address is for one
 * order, and it goes in once per cart, only into a cart without addresses, so what the buyer typed
 * in the checkout stays.
 */
function giveZip() {
  const give = zipToGive();
  if (!give) return Promise.resolve();
  zipping ??= (async () => {
    try {
      const data = await storefront('query OlinZip($id: ID!) { cart(id: $id) { delivery { addresses { id } } } }', {
        id: give.id,
      });
      if (!data?.cart) return;
      if (!data.cart.delivery.addresses.length) {
        await storefront(
          `mutation OlinZip($id: ID!, $addresses: [CartSelectableAddressInput!]!) {
            cartDeliveryAddressesAdd(cartId: $id, addresses: $addresses) { userErrors { message } }
          }`,
          {
            id: give.id,
            addresses: [
              { address: { deliveryAddress: { countryCode: 'UA', zip: give.zip } }, selected: true, oneTimeUse: true },
            ],
          }
        );
      }
      zipCart = give.id;
      try {
        sessionStorage.setItem(ZIP_DONE, give.id);
      } catch (_) {
        // Storage is off: the page remembers it
      }
    } catch (_) {
      // No luck: the buyer types the code in the checkout
    } finally {
      zipping = undefined;
    }
  })();
  return zipping;
}

/* ───── Wiring ───── */

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const addButton = target.closest('[data-olin-cart-add]');
  if (addButton instanceof HTMLElement) {
    event.preventDefault();
    add(addButton);
    return;
  }

  const have = target.closest('[data-olin-cart-have]');
  if (have instanceof HTMLElement) {
    skip(have.dataset.olinCartHave ?? '');
    return;
  }

  const head = target.closest('[data-olin-opt-toggle]');
  if (head instanceof HTMLElement) {
    toggle(head);
    return;
  }

  const who = target.closest('[data-olin-who]');
  if (who instanceof HTMLElement) {
    const box = who.closest('[data-olin-opt]');
    if (box && who.getAttribute('aria-checked') !== 'true') {
      setWho(box, who.dataset.olinWho === 'other');
      syncNote(box.closest('[data-olin-cart-options]') ?? box);
      save();
    }
    return;
  }

  const open = target.closest('[data-olin-note-open]');
  if (open instanceof HTMLElement) {
    const note = open.closest('[data-olin-note]');
    const field = note?.querySelector('[data-olin-note-field]');
    if (field instanceof HTMLElement) {
      open.hidden = true;
      field.hidden = false;
      field.querySelector('textarea')?.focus();
    }
    return;
  }

  // Leaving for the checkout: what is still waiting goes now (the form sends it too)
  const checkout = target.closest('#checkout, [name="checkout"]');
  if (checkout) {
    flush();
    if (!recipientReady(checkout)) {
      event.preventDefault();
      return;
    }
    // The postal code isn't in the cart yet: the checkout waits a moment for it
    if (checkout instanceof HTMLButtonElement && checkout.form && zipToGive()) {
      event.preventDefault();
      const go = () => checkout.form?.requestSubmit(checkout);
      Promise.race([giveZip(), new Promise((resolve) => window.setTimeout(resolve, ZIP_WAIT))]).then(go, go);
    }
  }
});

document.addEventListener('change', (event) => {
  const input = event.target;
  if (input instanceof HTMLInputElement && input.type === 'checkbox' && input.dataset.olinAttr) {
    pendingAttributes[input.dataset.olinAttr] = input.checked ? 'Так' : '';
    noteChanged(input);
    save();
  }
});

document.addEventListener('input', (event) => {
  const field = event.target;
  // While someone types, a new render of the cart keeps their text
  const typed =
    (field instanceof HTMLInputElement && (field.type === 'text' || field.type === 'tel')) ||
    field instanceof HTMLTextAreaElement;
  if (typed && field.dataset.olinAttr) {
    field.setAttribute('data-skip-value-update', '');
    pendingAttributes[field.dataset.olinAttr] = field.value.trim();
    if (field instanceof HTMLInputElement && field.hasAttribute('data-olin-code')) checkCode(field, false);
    if (field instanceof HTMLInputElement && field.hasAttribute('data-olin-phone')) checkPhone(field, false);
    // «Вкажіть ім’я й телефон отримувача» goes away as the fields fill
    const box = field.closest('[data-olin-opt]');
    const need = box?.querySelector('[data-olin-rec-need]');
    if (box && need instanceof HTMLElement && !need.hidden) recipientNeed(box, true);
    noteChanged(field, TYPING_PAUSE);
    save(TYPING_PAUSE);
  }
});

document.addEventListener('focusout', (event) => {
  const field = event.target;
  if (!(field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement)) return;
  if (!field.dataset.olinAttr) return;
  if (field instanceof HTMLInputElement && field.hasAttribute('data-olin-code')) checkCode(field, true);
  if (field instanceof HTMLInputElement && field.hasAttribute('data-olin-phone')) checkPhone(field, true);
  flush().then(() => field.removeAttribute('data-skip-value-update'));
});

window.addEventListener('pagehide', () => flush(true));
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') flush(true);
});

// Every new render of the drawer or the page shows the rules and the dispatch day afresh
let frame = 0;
const observer = new MutationObserver(() => {
  if (frame) return;
  frame = window.requestAnimationFrame(() => {
    frame = 0;
    if (!document.querySelector('[data-olin-cart-suggest], [data-olin-dispatch], [data-olin-zip]')) return;
    showRules();
    showDispatch();
    syncNotes();
    giveZip();
    observer.takeRecords();
  });
});

/** Notes that lag behind the options, e.g. a gift box put in by «Створи свій набір», go to the cart */
function syncNotes() {
  let changed = false;
  for (const options of document.querySelectorAll('[data-olin-cart-options]')) {
    if (syncNote(options)) changed = true;
  }
  if (changed) save();
}
observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden'] });

showRules();
showDispatch();
syncNotes();
giveZip();
